/*
 * Copyright 2020, IntraLinks, Inc. All rights reserved.
 */

// eslint-disable-next-line import/prefer-default-export
export const ENDPOINT_PREFIX = '/iam'; // e.g. '/myapp'
export const ERROR_PATH_403 = '/errors/403';
export const getErrorPath = (error: string): string => `/errors/${error}`;
export const unauthenticatedPaths = ['/error', '/eula', '/login-consent', '/errors'];

export enum I18nNamespaces {
  DEFAULT = 'default',
  COUNTER = 'counter',
  ADMIN = 'admin',
  POPUP = 'popup',
  ERROR = 'error',
  PROVIDER = 'provider',
  PROFILE = 'profile',
  COUNTRIES_NAMES = 'countryNames',
  LOA_ENRICHMENT = 'loaEnrichment'
}

export enum AuthenticatorType {
  GOOGLE = 'GOOGLE', OKTA = 'OKTA', INTRALINKS = 'INTRALINKS'
}

export const LoaAuthenticatorType = {
  PreferredAuthenticator: 'PreferredAuthenticator',
  Telesign: 'Telesign',
  NotificationService: 'NotificationService'
} as const;

export type LoaAuthenticatorType = typeof LoaAuthenticatorType[keyof typeof LoaAuthenticatorType];

export const NUMBER_REGEX = /[^0-9 \-.()]|[.\- ()]{2}/g;

export const SUPPORT_URL = 'https://www.intralinks.com/contact/client-services';
export const SECRET_TYPES = {
  CLIENT_ID_SECRET: 'Client Id And Secret Pair',
  PUBLIC_KEY: 'Public Key',
  PUBLIC_PRIVATE_KEY_PAIR: 'Public And Private Key Pair'
};

export enum FileUploadStatus {
  Edit = 'edit',
  Uploading = 'uploading',
  Complete = 'complete'
}

export const EULA_ACTIONS = {
  ACCEPT: 'ACCEPT',
  DECLINE: 'DECLINE'
};

export const CSRF_HEADER_NAME = 'x-il-csrf-token';
type AxiosHeaderMethod =
  | 'get'
  | 'post'
  | 'put'
  | 'patch'
  | 'delete'
  | 'head'
  | 'options';

export const CSRF_INCLUSION_METHODS: AxiosHeaderMethod[] = [
  'post',
  'patch',
  'put',
  'delete'
];

export const ERROR_PATHS = [
  '401',
  '404',
  '500',
  '403',
  '400',
  '405',
  '406',
  '409',
  '415',
  '417',
  '418',
  '502',
  '503',
  'Maintenance', 'maintenance'
];
export const URL_REGEX = '^(?:http(s)?:\\/\\/)?[\\w.-]+(?:\\.[\\w\\.-]+)+[\\w\\-\\._~:/?#[\\]@!\\$&\'\\(\\)\\*\\+,;=.]+$';
export const EULA_ITEMS: { [key: string]: number } = {
  accessToTheIntralinksService: 7,
  ownershipOfTheIntralinksService: 8,
  confidentiality: 2,
  terminationAccessToService: 2,
  complianceWithLaws: 1,
  limitedWarranty: 4,
  data: 3,
  dataPrivacy: 2,
  disclaimerOfLiability: 3,
  indemnificationRelatingToPostedData: 1,
  exportOfStandaloneUtilities: 1,
  remedy: 1,
  jurisdiction: 1,
  assignment: 1,
  survival: 1,
  miscellaneous: 1,
  changesToThisAgreement: 1,
  notices: 1,
  marketing: 4
};

export const EULA_HREF: { [key: string]: { [key: string]: string } } = {
  dataPrivacy1: { here: 'https://www.intralinks.com/privacy' },
  marketing1: { PrivacyPolicy: 'https://www.intralinks.com/privacy' },
  marketing2: { here: 'https://www3.intralinks.com/email-preferences' }
};

// Email Preferences Constants
export const DEAL_CENTRE_APP_ID = 'd1c5ffd8-560e-4962-8017-eebfd9fddc25';
export const EmailFrequencyList = ['IMMEDIATE', 'DAILY', 'NEVER'] as const;

// LoA Enrichment Constants
export const FORBIDDEN_CHARACTERS_REGEX = /[\\/\\:\\*"<>\\?\\|]+/;
export const DEFAULT_MIN_REQUIRED_LOA_LEVEL = 2;
export const DEFAULT_SECRET_KEY_LENGTH = 6;
export const DEFAULT_SMS_DIGITS = 6;
export const DEFAULT_EMAIL_DIGITS = 6;
export const DEFAULT_POPUP_CLOSE_TIME = 5;
export const LABEL_MAX_LENGTH = 50;
export const LOA_RESEND_SECONDS = 30;
export const LOA_TWO = 2;
export const LOA_THREE = 3;
export const DEFAULT_EMAIL_MASK_KEEP = 3;
export const DEFAULT_EMAIL_MASK_STARS = 5;

export const FactorStatuses = {
  ACTIVE: 'ACTIVE',
  PENDING_ACTIVATION: 'PENDING_ACTIVATION'
} as const;

export type FactorStatus = keyof typeof FactorStatuses;

export const ValidationErrorKey = {
  // common
  OtpInvalid: 'otpInvalid',
  OtpExpired: 'otpExpired',

  // sms enrollment
  SmsInvalidPhone: 'smsInvalidPhone',

  // sms code step
  InvalidSMSCode: 'invalidSMSCode',
  SMSFailed: 'smsFailed',

  // email
  InvalidEmailCode: 'invalidEmailCode',

  // labels
  LabelRequired: 'labelRequired',
  LabelTooLong: 'labelTooLong',
  LabelForbiddenChars: 'labelForbiddenChars'
} as const;

export type ValidationErrorKey = typeof ValidationErrorKey[keyof typeof ValidationErrorKey];

// LoA Elevation Constants
export const LOA_ELEVATION_TARGET_LEVEL = 2;
export const LoaElevationActions = { add: 'add' } as const;
export type LoaElevationAction = keyof typeof LoaElevationActions;
